// ============================================================================
// UNIVERSAL ACTION HANDLERS - TYPES
// ============================================================================

import type { DetailActionContext } from "@/contexts/DetailActionContext/types";

/**
 * Universal action handlers that work across all entity types.
 *
 * `item` stays loose (`any`): one handler map serves all 11 entities, so the
 * item cannot be a single concrete codegen type. `context` is fully typed as
 * `DetailActionContext<any>` so every handler's `context.onUpdateItem` /
 * `onOpenModal` / `onToast` / `entityName` call is compile-checked — method-name
 * and arity mistakes are now tsc errors. Per-entity FIELD-level write checking
 * lives at the concrete hook layer (`useContactHooks(): EntityHooks<Contacts>`,
 * etc.), where `mutateAsync(data: Partial<Contacts>)` is already schema-checked.
 * `<any>` (not `<BaseEntity>`) sidesteps the inherent onUpdateItem variance when
 * a concrete `DetailActionContext<T>` is passed in.
 */
export interface UniversalActionHandlers {
  [key: string]: (
    item: any,
    context: DetailActionContext<any>
  ) => Promise<void> | void;
}

/**
 * Debug helper to log modal data being passed
 */
export function debugModalData(
  actionName: string,
  modalId: string,
  data: Record<string, unknown>
) {
  if (typeof window !== "undefined" && process.env.NODE_ENV === "development") {
    console.group(`🎬 Action: ${actionName} → Modal: ${modalId}`);
    console.log("Data being passed:", data);
    console.log("Data keys:", Object.keys(data));
    console.groupEnd();
  }
}
